import {
  Box,
  Button,
  Chip,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { AdminPanelSettings } from "@mui/icons-material";
import { PlatformUser } from "types/interfaces/adminUsers";
import { ReactNode } from "react";
import { GetAdminUsersParams } from "features/adminUsers/adminUsers";
import CustomScrollbars from "../CustomScrollbars";

interface UserManagementTableProps {
  users: PlatformUser[];
  isLoading: boolean;
  isFetching: boolean;
  selectedUserId?: number;
  onManage: (user: PlatformUser) => void;
  pagination?: ReactNode;
  sortBy: GetAdminUsersParams["sort_by"];
  sortOrder: GetAdminUsersParams["sort_order"];
  onSortChange: (field: GetAdminUsersParams["sort_by"]) => void;
  hasUnsavedChangesForUser?: (userId: number) => boolean;
}

function RoleChips({ user }: { user: PlatformUser }) {
  const { profile } = user;
  const chips: { label: string; sx: object; icon?: React.ReactElement }[] = [];

  if (profile.is_superuser) {
    chips.push({
      label: "Super Admin",
      icon: <AdminPanelSettings sx={{ fontSize: 16, color: "inherit !important" }} />,
      sx: { bgcolor: "primary.main", color: "primary.contrastText", fontWeight: 600 },
    });
  }
  if (profile.is_calibrate_manager) {
    chips.push({ label: "Manager", sx: { bgcolor: "#f3e5f5", color: "#6a1b9a", fontWeight: 600 } });
  }
  if (profile.is_data_storage_user) {
    chips.push({ label: "Data Storage", sx: { bgcolor: "#e3f2fd", color: "#1565c0", fontWeight: 600 } });
  }
  if (profile.is_integration_dashboard_user) {
    chips.push({ label: "Integration", sx: { bgcolor: "#fff3e0", color: "#e65100", fontWeight: 600 } });
  }
  if (profile.is_ai_agent_user) {
    chips.push({ label: "AI Agent", sx: { bgcolor: "#e0f2f1", color: "#00695c", fontWeight: 600 } });
  }

  return (
    <Box display="flex" gap={0.5} flexWrap="wrap" alignItems="center">
      {chips.length > 0
        ? chips.map((c) => (
            <Chip key={c.label} icon={c.icon} label={c.label} size="small" sx={c.sx} />
          ))
        : <Typography variant="caption" color="text.disabled">—</Typography>}
    </Box>
  );
}

function SkeletonRows() {
  return (
    <>
      {Array.from({ length: 8 }).map((_, i) => (
        <TableRow key={i}>
          {Array.from({ length: 8 }).map((_, j) => (
            <TableCell key={j}>
              <Skeleton variant="text" width={j === 2 ? 120 : "80%"} />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </>
  );
}

export default function UserManagementTable({
  users,
  isLoading,
  isFetching,
  selectedUserId,
  onManage,
  pagination,
  sortBy,
  sortOrder,
  onSortChange,
  hasUnsavedChangesForUser,
}: UserManagementTableProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <CustomScrollbars sx={{ flex: 1, minHeight: 0 }} trackOffsetTop={40}>
        <Table stickyHeader size="small" sx={{ minWidth: 680 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>
                <TableSortLabel
                  active={sortBy === "name"}
                  direction={sortBy === "name" ? sortOrder ?? "asc" : "asc"}
                  onClick={() => onSortChange("name")}
                >
                  Name
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 700 }}>
                <TableSortLabel
                  active={sortBy === "email"}
                  direction={sortBy === "email" ? sortOrder ?? "asc" : "asc"}
                  onClick={() => onSortChange("email")}
                >
                  Email
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Roles</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>
                <TableSortLabel
                  active={sortBy === "folders"}
                  direction={sortBy === "folders" ? sortOrder ?? "asc" : "asc"}
                  onClick={() => onSortChange("folders")}
                >
                  Folders
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 700 }}>
                <TableSortLabel
                  active={sortBy === "integrations"}
                  direction={sortBy === "integrations" ? sortOrder ?? "asc" : "asc"}
                  onClick={() => onSortChange("integrations")}
                >
                  Integrations
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 700 }}>
                <TableSortLabel
                  active={sortBy === "date_joined"}
                  direction={sortBy === "date_joined" ? sortOrder ?? "asc" : "asc"}
                  onClick={() => onSortChange("date_joined")}
                >
                  Created
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 700 }}>
                <TableSortLabel
                  active={sortBy === "last_login"}
                  direction={sortBy === "last_login" ? sortOrder ?? "asc" : "asc"}
                  onClick={() => onSortChange("last_login")}
                >
                  Last Login
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <SkeletonRows />
            ) : users.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                  <Typography color="text.secondary">No users found</Typography>
                </TableCell>
              </TableRow>
            ) : (
              users.map((user) => {
                const isArchived = user.profile.is_archived;
                const hasUnsaved = typeof hasUnsavedChangesForUser === "function" ? hasUnsavedChangesForUser(user.id) : false;
                return (
                  <TableRow
                    key={user.id}
                    selected={user.id === selectedUserId}
                    sx={{
                      opacity: isFetching ? 0.6 : 1,
                      "&.Mui-selected": { bgcolor: "rgba(31,93,107,0.06)" },
                      "&.Mui-selected:hover": { bgcolor: "rgba(31,93,107,0.10)" },
                    }}
                  >
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <Typography variant="body2" fontWeight={600}>
                          {user.first_name || user.last_name
                            ? `${user.first_name} ${user.last_name}`.trim()
                            : user.username}
                        </Typography>
                        {isArchived && (
                          <Chip
                            label="Archived"
                            size="small"
                            sx={{
                              height: 16,
                              fontSize: 10,
                              fontWeight: 700,
                              bgcolor: "rgba(211,47,47,0.10)",
                              color: "error.dark",
                            }}
                          />
                        )}
                      </Box>
                      {(user.first_name || user.last_name) && (
                        <Typography variant="caption" color="text.secondary">
                          @{user.username}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ opacity: isArchived ? 0.5 : 1 }}>
                      <Typography variant="body2">{user.email || "—"}</Typography>
                    </TableCell>
                    <TableCell sx={{ opacity: isArchived ? 0.5 : 1 }}>
                      <Box display="flex" gap={0.5} flexWrap="wrap" alignItems="center">
                        <RoleChips user={user} />
                        {hasUnsaved && !isArchived && (
                          <Chip
                            label="Unsaved"
                            size="small"
                            sx={{
                              fontWeight: 600,
                              bgcolor: "#fce4ec",
                              color: "#ad1457",
                            }}
                          />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ opacity: isArchived ? 0.5 : 1 }}>
                      <Typography variant="body2">{user.folder_count}</Typography>
                    </TableCell>
                    <TableCell sx={{ opacity: isArchived ? 0.5 : 1 }}>
                      <Typography variant="body2">{user.integration_count}</Typography>
                    </TableCell>
                    <TableCell sx={{ opacity: isArchived ? 0.5 : 1 }}>
                      <Typography variant="body2">
                        {new Date(user.date_joined).toLocaleDateString()}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ opacity: isArchived ? 0.5 : 1 }}>
                      <Typography variant="body2">
                        {user.last_login
                          ? new Date(user.last_login).toLocaleString(undefined, {
                              dateStyle: "short",
                              timeStyle: "short",
                            })
                          : "—"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Button
                        size={isMobile ? "medium" : "small"}
                        variant={user.id === selectedUserId ? "contained" : "outlined"}
                        color="primary"
                        onClick={() => onManage(user)}
                        sx={{
                          textTransform: "none",
                          fontWeight: 700,
                          minHeight: isMobile ? 40 : undefined,
                          px: isMobile ? 2 : undefined,
                        }}
                      >
                        Manage
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </CustomScrollbars>
      {pagination}
    </Box>
  );
}
